import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import PageTitle from "../../../layouts/PageTitle";
import { selectUserRole } from "../../../../store/authSlice";
import { useGetNcsEmployeeDailyQuery } from "../../../../store/appApi";
import {
  usePartnerCode,
  PartnerPicker,
  panelClass,
  fieldClass,
  labelClass,
  fmtDate,
  fmtDateInput,
} from "./config";
import EmployeeDayModal from "./EmployeeDayModal";

const WHITE_FIELD_STYLE = { backgroundColor: "#fff", color: "#1f2937" };

const monthStart = () => {
  const d = new Date();
  return fmtDateInput(new Date(d.getFullYear(), d.getMonth(), 1));
};

const NcsEmployeeDaily = () => {
  const role = useSelector(selectUserRole);
  const [partnerCode, setPartnerCode] = usePartnerCode();
  // "All partners" is local to this screen only — never written to the
  // shared, cross-page selected partner (usePartnerCode/localStorage), same
  // reasoning as the Followups board's viewAll.
  // Defaults to every partner: an employee's day is about the person, and
  // many work across several partners — starting on whichever single partner
  // was last picked elsewhere silently hid most of their activity.
  const [viewAll, setViewAll] = useState(true);
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(fmtDateInput());
  // Which day's customer-activity modal is open, if any.
  const [modal, setModal] = useState(null); // { employeeName, employeeRole, date, items }

  const { data, isFetching } = useGetNcsEmployeeDailyQuery(
    { partnerCode: viewAll ? "ALL" : partnerCode, from, to },
    { skip: role !== "super-admin" },
  );

  const actions = useMemo(() => data?.actions || [], [data]);
  const rows = useMemo(() => data?.rows || [], [data]);
  const employeeTotals = data?.employeeTotals || [];
  const grandTotal = data?.grandTotal;

  // Only show the "Other" column when something actually landed there.
  const showOther = useMemo(
    () =>
      (grandTotal?.byAction?.other || 0) > 0 ||
      rows.some((r) => (r.byAction?.other || 0) > 0),
    [grandTotal, rows],
  );

  const cols = useMemo(
    () => [
      ...actions,
      ...(showOther ? [{ key: "other", label: "Other" }] : []),
    ],
    [actions, showOther],
  );

  // action key -> display label, for the per-day customer modal.
  const actionLabel = useMemo(
    () => Object.fromEntries(actions.map((a) => [a.key, a.label])),
    [actions],
  );

  const rowsByUser = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => {
      if (!m.has(r.userId)) m.set(r.userId, []);
      m.get(r.userId).push(r);
    });
    m.forEach((list) => list.sort((a, b) => b.date.localeCompare(a.date)));
    return m;
  }, [rows]);

  if (role !== "super-admin") {
    return (
      <>
        <PageTitle activeMenu="Employee Daily" motherMenu="Netplus" />
        <div className={`${panelClass} p-10 text-center`}>
          <h3 className="text-lg font-semibold text-gray-900">Super-admin only</h3>
        </div>
      </>
    );
  }

  const colCount = 2 + cols.length + 1;

  // Items behind one number. colKey: an action key, "other" (anything not a
  // listed column) or null (the Total column = everything).
  const pickItems = (dayRows, colKey) =>
    dayRows
      .flatMap((r) => r.items || [])
      .filter((it) => {
        if (!colKey) return true;
        if (colKey === "other") return !actionLabel[it.action];
        return it.action === colKey;
      })
      .sort((a, b) => new Date(b.at) - new Date(a.at));
  const openList = (emp, dayRows, colKey, date) =>
    setModal({
      employeeName: emp ? emp.name : "All employees",
      employeeRole: emp ? emp.role : "",
      date,
      title: colKey ? cols.find((c) => c.key === colKey)?.label || "Other" : "All activity",
      items: pickItems(dayRows, colKey),
    });
  // A number is a link when it's non-zero.
  const Num = ({ value, onClick, className = "" }) =>
    value ? (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        className={`tabular-nums underline decoration-dotted underline-offset-2 hover:text-cyan-700 ${className}`}
      >
        {value}
      </button>
    ) : (
      <span className={className}>{value || 0}</span>
    );

  return (
    <>
      <PageTitle activeMenu="Employee Daily" motherMenu="Netplus" />
      <section className={panelClass}>
        <div className="flex flex-wrap items-end gap-3 border-b border-gray-100 px-5 py-3">
          <div>
            <span className={labelClass}>From</span>
            <input
              type="date"
              className={`${fieldClass} w-40`}
              style={WHITE_FIELD_STYLE}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div>
            <span className={labelClass}>To</span>
            <input
              type="date"
              className={`${fieldClass} w-40`}
              style={WHITE_FIELD_STYLE}
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <div className="ml-auto">
            <PartnerPicker
              allowAll
              value={viewAll ? "ALL" : partnerCode}
              onChange={(v) => {
                if (v === "ALL") {
                  setViewAll(true);
                } else {
                  setViewAll(false);
                  setPartnerCode(v);
                }
              }}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                <th className="px-4 py-3 text-left">Employee</th>
                <th className="px-4 py-3 text-left">Day</th>
                {cols.map((c) => (
                  <th key={c.key} className="px-3 py-3 text-center">
                    {c.label}
                  </th>
                ))}
                <th className="px-4 py-3 text-center">Total</th>
              </tr>
            </thead>

            {isFetching ? (
              <tbody>
                <tr>
                  <td colSpan={colCount} className="px-4 py-12 text-center text-gray-400">
                    Loading…
                  </td>
                </tr>
              </tbody>
            ) : employeeTotals.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan={colCount} className="px-4 py-12 text-center text-gray-400">
                    No logged activity in this range.
                  </td>
                </tr>
              </tbody>
            ) : (
              employeeTotals.map((emp) => {
                const days = rowsByUser.get(emp.userId) || [];
                return (
                  <tbody
                    key={emp.userId}
                    className="divide-y divide-gray-100 border-b-4 border-gray-100"
                  >
                    {/* employee subtotal band */}
                    <tr className="bg-cyan-50/50 font-semibold text-gray-800">
                      <td className="px-4 py-2.5">
                        {emp.name}
                        {emp.role ? (
                          <span className="ml-1 text-xs font-normal text-gray-400">
                            {emp.role}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-500">
                        {emp.days} day{emp.days === 1 ? "" : "s"}
                      </td>
                      {cols.map((c) => (
                        <td
                          key={c.key}
                          className="px-3 py-2.5 text-center tabular-nums"
                        >
                          <Num
                            value={emp.byAction?.[c.key] || 0}
                            onClick={() => openList(emp, days, c.key)}
                          />
                        </td>
                      ))}
                      <td className="px-4 py-2.5 text-center tabular-nums text-cyan-700">
                        <Num value={emp.total} onClick={() => openList(emp, days, null)} />
                      </td>
                    </tr>

                    {/* per-day rows — click opens the customer-activity modal */}
                    {days.map((r) => {
                      const key = `${r.userId}|${r.date}`;
                      return (
                        <tr
                          key={key}
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() =>
                            setModal({
                              employeeName: emp.name,
                              employeeRole: emp.role,
                              date: r.date,
                              items: r.items || [],
                            })
                          }
                        >
                          <td className="px-4 py-2 text-right text-xs font-semibold text-cyan-600">
                            View
                          </td>
                          <td className="px-4 py-2 font-medium text-gray-700">
                            {fmtDate(r.date)}
                          </td>
                          {cols.map((c) => (
                            <td
                              key={c.key}
                              className={`px-3 py-2 text-center tabular-nums ${
                                r.byAction?.[c.key]
                                  ? "text-gray-800"
                                  : "text-gray-300"
                              }`}
                            >
                              <Num
                                value={r.byAction?.[c.key] || 0}
                                onClick={() => openList(emp, [r], c.key, r.date)}
                              />
                            </td>
                          ))}
                          <td className="px-4 py-2 text-center font-semibold tabular-nums text-gray-800">
                            <Num value={r.total} onClick={() => openList(emp, [r], null, r.date)} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                );
              })
            )}

            {grandTotal && employeeTotals.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-gray-300 bg-gray-100 font-bold text-gray-800">
                  <td className="px-4 py-3 text-xs uppercase tracking-wider text-gray-500">
                    Grand total
                  </td>
                  <td />
                  {cols.map((c) => (
                    <td key={c.key} className="px-3 py-3 text-center tabular-nums">
                      <Num
                        value={grandTotal.byAction?.[c.key] || 0}
                        onClick={() => openList(null, rows, c.key)}
                      />
                    </td>
                  ))}
                  <td className="px-4 py-3 text-center tabular-nums">
                    <Num value={grandTotal.total} onClick={() => openList(null, rows, null)} />
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </section>

      <EmployeeDayModal
        show={!!modal}
        onClose={() => setModal(null)}
        employeeName={modal?.employeeName}
        employeeRole={modal?.employeeRole}
        date={modal?.date}
        title={modal?.title}
        items={modal?.items}
        actionLabel={actionLabel}
      />
    </>
  );
};

export default NcsEmployeeDaily;
